import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { cacheGet, cacheSet, BLOG_POSTS_KEY } from '../../utils/cache';
import "./Blog.css";

const GHOST_URL = process.env.REACT_APP_GHOST_URL || 'https://blog.microled.com.ar';
const GHOST_KEY = process.env.REACT_APP_GHOST_API_KEY || '6f4e712d57168c2554f6d1982f';

async function fetchGhostPosts() {
  const url = `${GHOST_URL}/ghost/api/content/posts/?key=${GHOST_KEY}&limit=all&include=tags,authors`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Ghost API error: ${res.status}`);
  const data = await res.json();
  return data.posts || [];
}

// --- Componente para el esqueleto de carga ---
const SkeletonCard = () => (
  <div className="card-skeleton">
    <div className="skeleton-image"></div>
    <div className="skeleton-content">
      <div className="skeleton-title"></div>
    </div>
  </div>
);
const SkeletonBanner = () => <div className="skeleton-banner"></div>;

const Blog = () => {
  const [posts, setPosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      // Limpiar cache viejo (puede tener [] vacío de versiones anteriores)
      const cached = cacheGet(BLOG_POSTS_KEY);
      if (cached && cached.length > 0) {
        setPosts(cached);
        setLoading(false);
        return;
      }
      try {
        const fetchedPosts = await fetchGhostPosts();
        if (fetchedPosts.length > 0) {
          setPosts(fetchedPosts);
          cacheSet(BLOG_POSTS_KEY, fetchedPosts);
        }
      } catch (err) {
        console.error('Ghost API error:', err.message);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-AR', options);
  };

  const featuredPost = posts[0];

  return (
    <>
      <Helmet>
        <title>• Blog y Novedades | Microled Argentina</title>
        <meta name="description" content="Enterate de las últimas noticias, proyectos y novedades del mundo de la cartelería digital y pantallas led en nuestro blog." />
      </Helmet>
       
      <div className="blog-container">
        <header className="blog-header">
          <h1>Microled News</h1>
        </header>

        {loading ? (
          <>
            <SkeletonBanner />
            <div className="posts-grid">
              {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          </>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#cb3234' }}>
            <p>⚠️ No se pudieron cargar las notas</p>
            <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.5rem' }}>{error}</p>
          </div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            <p>No hay notas publicadas todavía.</p>
          </div>
        ) : (
          posts.length > 0 && (
            <>
              <Link to={`/blog/${featuredPost.slug}`} className="featured-post-link">
                <article className="featured-post-banner" style={{backgroundImage: `url(${featuredPost.feature_image})`}}>
                  <div className="featured-post-content">
                    <span className="featured-tag">Lo último</span>
                    <h2>{featuredPost.title}</h2>
                    <div className="post-meta">
                      {featuredPost.primary_author?.name && <span>Por {featuredPost.primary_author.name}</span>}
                      <span>•</span>
                      <time dateTime={featuredPost.published_at}>{formatDate(featuredPost.published_at)}</time>
                    </div>
                  </div>
                </article>
              </Link>
              
              <div className="all-posts-title">
                <h3>Todas las publicaciones</h3>
              </div>
              <div className="posts-grid">
                {posts.map((post, index) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="post-card-link" style={{'--i': index}}>
                    <article className="post-card">
                      <div className="post-card-image" style={{backgroundImage: `url(${post.feature_image})`}}></div>
                      <div className="post-card-content">
                        <h3>{post.title}</h3>
                        <div className="post-card-footer">
                          {post.primary_author?.name && <span className="author">Por {post.primary_author.name}</span>}
                          <time className="date" dateTime={post.published_at}>{formatDate(post.published_at)}</time>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </>
          )
        )}
      </div>
    </>
  );
}

export default Blog;